import { getMessaging, onMessage } from "firebase/messaging";
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useSnackbar } from "../../hooks/useSnackbar";
import { firebaseApp } from "../../services/firebase/firebase";

interface NotificationMessage {
  title: string;
  /* TODO : need to fix type any remove this later */
  message: any;
  unread_count?: string;
}

type INotification<K extends string, P> = {
  type: K;
} & P;

export type Notification = INotification<"notification", NotificationMessage>;

/**
 * Listens to firebase notifications.
 * Notifications are streamed to the caller via a callback.
 *
 * @param listener the callback to place when a message is received.
 */
export default function useFcmEvents(
  listener: (notification: Notification) => void
) {
  const fcm = useFcmContext();

  useEffect(() => {
    fcm.addListener(listener);

    return () => fcm.removeListener(listener);
  }, [fcm]);
}

type ListenerFn = (notification: Notification) => void;

type FcmContext = {
  addListener: (listener: ListenerFn) => void;
  removeListener: (listener: ListenerFn) => void;
};

/**
 * FcmContext is a context for the FcmProvider
 */
const FcmContext = createContext<FcmContext | undefined>(undefined);

/**
 * FcmProvider is a context provider for the FcmContext
 */
export const FcmProvider: React.FC<PropsWithChildren> = ({ children }) => {
  /**
   * list of listeners to notify about new notifications
   */
  const listeners = useRef<ListenerFn[]>([]);

  const onNotification = (notification: Notification) => {
    listeners.current.forEach((fn) => fn(notification));
  };

  const showSnackbar = useSnackbar();

  useEffect(() => {
    const messaging = getMessaging(firebaseApp);

    const unsubscribe = onMessage(messaging, (message) => {
      if (message?.notification) {
        showSnackbar({
          message: message?.notification?.title ?? "",
          type: "success",
        });
      }

      const data = message.data;
      const notification = message.notification;

      if (notification) {
        onNotification({
          type: "notification",
          title: notification.title ?? "",
          message,
        });
      }

      if (data && data.notification_type) {
        onNotification(data as unknown as Notification);
      }
    });

    return unsubscribe;
  }, []);

  /** add a listener to the list of listeners */
  const addListener = (listener: ListenerFn) =>
    listeners.current.push(listener);

  /** remove a listener from the list of listeners */
  const removeListener = (listener: ListenerFn) =>
    (listeners.current = listeners.current.filter((it) => it !== listener));

  const context: FcmContext = {
    addListener,
    removeListener,
  };

  return <FcmContext.Provider value={context}>{children}</FcmContext.Provider>;
};

/**
 * useFcmContext is a hook to get the FcmContext
 */
export const useFcmContext = (): FcmContext => {
  const fcmContext = useContext(FcmContext);

  if (!fcmContext) throw new Error("FcmContext was not provided.");

  return fcmContext;
};
